import { ASSIST_LIVE, ASSIST_PAUSED } from '../protocol/presenceMessages';
import { consentAllowsMic } from './wearerAudioPolicy';

/** The consent machine's states (Plan-01 §2.2). */
export type AssistConsentState =
  /** No assist session (room down / master off). */
  | 'off'
  /** Session up, config's autoAllow=false - waiting for the hand menu's "Allow assistance". */
  | 'awaitingAllow'
  /** Mic published (unmuted), panels visible, indicator lit. */
  | 'live'
  /** Master pause: mic muted, assistant AV unsubscribed, indicator amber. */
  | 'paused';

/**
 * The per-session consent state machine (Plan-01 step 6, RA-C6 / D6). Port of
 * `AssistConsentMachine.cs`.
 *
 *              config assist.autoAllow=true                 Pause (hand menu)
 *   off ------------------------------------> live <------------------------> paused
 *    ^    autoAllow=false -> (wait for hand-menu Allow)
 *    +--- session end / master off
 *
 * Pure state - the session service applies the consequences (mic publish,
 * subscriptions, indicator, presence broadcast) on the change callback.
 */
export class AssistConsentMachine {
  private _state: AssistConsentState = 'off';
  private readonly listeners = new Set<(state: AssistConsentState) => void>();

  onStateChanged(listener: (state: AssistConsentState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get state(): AssistConsentState {
    return this._state;
  }

  /** The hand menu should offer "Allow assistance". */
  get awaitingAllow(): boolean {
    return this._state === 'awaitingAllow';
  }

  /**
   * Consent allows a microphone (RA-C2/RA-C6). NOT the same question as "is
   * the mic open" - the wearer's own mute also has a say, which is
   * `micShouldPublish` in wearerAudioPolicy. The rule itself lives there so
   * the two answers cannot drift apart.
   */
  get micShouldPublish(): boolean {
    return consentAllowsMic(this._state);
  }

  /**
   * The wire token for the `wearerState` presence broadcast: live/paused
   * while active, empty otherwise (the wire defines nothing else - an
   * inactive session broadcasts nothing).
   */
  get assistToken(): string {
    switch (this._state) {
      case 'live':
        return ASSIST_LIVE;
      case 'paused':
        return ASSIST_PAUSED;
      default:
        return '';
    }
  }

  /** The status token: off / live / paused (awaitingAllow reads "off"). */
  get statusToken(): string {
    switch (this._state) {
      case 'live':
        return 'live';
      case 'paused':
        return 'paused';
      default:
        return 'off';
    }
  }

  /** The assist session became possible (room connected). D6: config decides the entry. */
  sessionStarted(autoAllow: boolean): void {
    if (this._state !== 'off') return;
    this.set(autoAllow ? 'live' : 'awaitingAllow');
  }

  /** The hand menu's master action: Allow (awaiting) or Pause/Resume toggle. */
  masterAction(): void {
    switch (this._state) {
      case 'awaitingAllow':
        this.set('live');
        break;
      case 'live':
        this.set('paused');
        break;
      case 'paused':
        this.set('live');
        break;
      // off: nothing to act on - no session.
    }
  }

  /**
   * A late config arrival flips auto-allow on (RA-C6: the server config can
   * pre-authorise assistance). Only promotes a WAITING session - never
   * un-pauses, never overrides the wearer's own choices.
   */
  autoAllowUpdated(autoAllow: boolean): void {
    if (autoAllow && this._state === 'awaitingAllow') this.set('live');
  }

  /** The session ended (room dropped / master off). */
  sessionEnded(): void {
    if (this._state !== 'off') this.set('off');
  }

  private set(next: AssistConsentState): void {
    if (next === this._state) return;
    this._state = next;
    this.listeners.forEach((listener) => listener(next));
  }
}
